import type { ComponentType } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import {
    fetchDynamicsExportSettingsThunk,
    getDynamicsColumnsThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/export";
import {
    fetchHsExportSettingsThunk,
    getHsColumnsThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/export";
import {
    fetchSfExportSettingsThunk,
    getSfColumnsThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/export";
import { selectDynamicsCurrentSettings } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamics/exportSelectors";
import { selectHsCurrentSettings } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import { selectSfCurrentSettings } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

const selectSettingsConfig = {
    [ExportIntegrations.hubspot]: selectHsCurrentSettings,
    [ExportIntegrations.salesforce]: selectSfCurrentSettings,
    [ExportIntegrations.dynamics]: selectDynamicsCurrentSettings,
};

const useCrmSettings = () => {
    const { integrationName } = getIntegrationNameFromUrl() as { integrationName: Integration };
    return useSelector(selectSettingsConfig[integrationName]);
};

export const withMappingsAndColumnsFetch =
    <P extends object>({ refetchIfExists }: { refetchIfExists: boolean }) =>
    (WrappedComponent: ComponentType<P>) => {
        const WithMappingFetchingWrapper = (props: P) => {
            const { integrationName } = getIntegrationNameFromUrl();
            const dispatch = useDispatch();
            const settings = useCrmSettings();

            useMount(() => {
                const isEmpty = settings.length === 0;
                const shouldRefetch = isEmpty || refetchIfExists;
                if (!integrationName || !shouldRefetch) {return;}

                const abortController = new AbortController();

                if (integrationName === "hubspot") {
                    dispatch(fetchHsExportSettingsThunk(abortController.signal));
                    dispatch(getHsColumnsThunk(abortController.signal));
                } else if (integrationName === "salesforce") {
                    dispatch(fetchSfExportSettingsThunk(abortController.signal));
                    dispatch(getSfColumnsThunk(abortController.signal));
                } else if (integrationName === "dynamics") {
                    dispatch(fetchDynamicsExportSettingsThunk(abortController.signal));
                    dispatch(getDynamicsColumnsThunk(abortController.signal));
                }

                return () => abortController.abort();
            });

            return <WrappedComponent {...props} />;
        };

        return WithMappingFetchingWrapper;
    };
